import { useEffect } from "react";
import ReactGA from "react-ga4";

/**
 * Detects the origin of traffic by analyzing the user agent and referrer.
 * Now better distinguishes between direct traffic, known sources, and unknown sources.
 */
function detectTrafficOrigin(): string {
    const ua = navigator.userAgent.toLowerCase();
    const ref = document.referrer.toLowerCase();

    // If no referrer, it could be direct traffic or privacy-stripped
    if (!ref) {
        // Check user agent to see if we can infer the source
        if (ua.includes("slack")) return "slack";
        if (ua.includes("teams")) return "teams";
        if (ua.includes("linkedin")) return "linkedin_app";
        if (ua.includes("instagram")) return "instagram_app";
        if (ua.includes("facebook")) return "facebook_app";
        
        // If no app detected, label as direct/stripped referrer
        return "direct_or_privacy_stripped";
    }

    // Check referrer for known sources
    if (ref.includes("mail.") || ref.includes("outlook") || ref.includes("gmail")) return "email";
    if (ref.includes("google.") && !ref.includes("mail")) return "google_search";
    if (ref.includes("bing.")) return "bing_search";
    if (ref.includes("yahoo.")) return "yahoo_search";
    if (ref.includes("linkedin.")) return "linkedin";
    if (ref.includes("facebook.") || ref.includes("fb.")) return "facebook";
    if (ref.includes("twitter.") || ref.includes("t.co")) return "twitter";
    if (ref.includes("instagram.")) return "instagram";
    if (ref.includes("youtube.")) return "youtube";
    if (ref.includes("reddit.")) return "reddit";
    
    // Check user agent for app-based browsing with referrer
    if (ua.includes("slack")) return "slack";
    if (ua.includes("teams")) return "teams";

    // Referrer exists but doesn't match known sources
    return "other_referrer";
}

/**
 * Calculates how "deep" the user landed in your site structure.
 * Example: "/" = 0, "/about" = 1, "/services/consulting" = 2
 * 
 * This helps identify if users are landing on deep pages (shared links, SEO)
 * versus shallow pages (homepage navigation).
 */
function landingDepth(): number {
    return window.location.pathname.split("/").filter(Boolean).length;
}

/**
 * Stores and retrieves the first page path where the user entered the site during this session.
 * Uses sessionStorage to persist across page navigations but not browser restarts.
 * 
 * This helps distinguish between the actual entry point and subsequent page views.
 */
function getEntryPath(): string {
    const stored = sessionStorage.getItem("entry_path");
    if (stored) return stored;

    sessionStorage.setItem("entry_path", window.location.pathname);
    return window.location.pathname;
}

/**
 * Detects UTM parameters for marketing attribution.
 * Returns the utm_source value or "none" if not present.
 * This is the most reliable way to track campaign sources.
 */
function getUtmSource(): string {
    const params = new URLSearchParams(window.location.search);
    return params.get("utm_source") || "none";
}

/**
 * Detects UTM campaign parameter.
 * Returns the utm_campaign value or "none" if not present.
 */
function getUtmCampaign(): string {
    const params = new URLSearchParams(window.location.search);
    return params.get("utm_campaign") || "none";
}

/**
 * Detects UTM medium parameter.
 * Returns the utm_medium value or "none" if not present.
 */
function getUtmMedium(): string {
    const params = new URLSearchParams(window.location.search);
    return params.get("utm_medium") || "none";
}

/**
 * Detects custom query parameters that hint at traffic source.
 * Looks for: ?pdf, ?share, ?ref, ?via
 * 
 * These params can help identify:
 * - PDF links (?pdf)
 * - Social shares (?share)
 * - Referral links (?ref)
 * - Attribution links (?via)
 * 
 * Returns comma-separated list or "none".
 */
function detectQueryHints(): string {
    const params = new URLSearchParams(window.location.search);
    const hints: string[] = [];

    if (params.has("pdf")) hints.push("pdf");
    if (params.has("share")) hints.push("share");
    if (params.has("ref")) hints.push("ref");
    if (params.has("via")) hints.push("via");

    return hints.join(",") || "none";
}

/**
 * Detects the app/platform context where the site is being viewed.
 * Checks user agent for embedded browsers within apps (Slack, Teams, LinkedIn, etc.)
 * 
 * Helps distinguish between:
 * - Native browser traffic
 * - In-app browser traffic (which may have stripped referrers)
 */
function detectAppContext(): string {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes("slack")) return "slack";
    if (ua.includes("teams")) return "teams";
    if (ua.includes("linkedin")) return "linkedin";
    if (ua.includes("instagram")) return "instagram";
    if (ua.includes("facebook")) return "facebook";

    return "browser";
}

/**
 * Captures the very first referrer of the session (first touch attribution).
 * Stores in sessionStorage so subsequent page loads don't overwrite it.
 * 
 * Now provides more context when referrer is empty by checking user agent.
 */
function getFirstTouch(): string {
    const stored = sessionStorage.getItem("first_touch");
    if (stored) return stored;

    let first = document.referrer;
    
    // If no referrer, check if we can infer from user agent
    if (!first) {
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes("slack")) first = "slack_no_referrer";
        else if (ua.includes("teams")) first = "teams_no_referrer";
        else if (ua.includes("linkedin")) first = "linkedin_no_referrer";
        else if (ua.includes("instagram")) first = "instagram_no_referrer";
        else if (ua.includes("facebook")) first = "facebook_no_referrer";
        else first = "direct_no_referrer";
    }
    
    sessionStorage.setItem("first_touch", first);
    return first;
}

/**
 * Provides an intelligent traffic source by combining multiple signals.
 * This is more accurate than relying on referrer alone.
 * Priority: UTM params > Referrer > User Agent > Query hints
 */
function getIntelligentSource(): string {
    // 1. UTM parameters are most reliable (explicitly set by marketers)
    const utmSource = getUtmSource();
    if (utmSource !== "none") return `utm_${utmSource}`;
    
    // 2. Check for known referrers
    const ref = document.referrer.toLowerCase();
    if (ref) {
        if (ref.includes("google.") && !ref.includes("mail")) return "organic_google";
        if (ref.includes("bing.")) return "organic_bing";
        if (ref.includes("linkedin.")) return "social_linkedin";
        if (ref.includes("facebook.") || ref.includes("fb.")) return "social_facebook";
        if (ref.includes("twitter.") || ref.includes("t.co")) return "social_twitter";
        if (ref.includes("mail.") || ref.includes("outlook") || ref.includes("gmail")) return "email_client";
    }
    
    // 3. Check user agent for app context
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("slack")) return "app_slack";
    if (ua.includes("teams")) return "app_teams";
    if (ua.includes("linkedin")) return "app_linkedin";
    
    // 4. Check query hints
    const params = new URLSearchParams(window.location.search);
    if (params.has("pdf")) return "direct_pdf_link";
    if (params.has("share")) return "direct_share_link";
    if (params.has("ref")) return "direct_referral_link";
    
    // 5. If we have a referrer but it's not recognized
    if (ref) return "other_referrer";
    
    // 6. No signals - truly direct or privacy-stripped
    return "direct_unknown";
}

/**
 * Initializes Google Analytics 4 with custom traffic attribution metadata.
 * 
 * WHAT THIS COMPONENT DOES:
 * 1. Runs once when the app mounts (useEffect with empty dependency array)
 * 2. Collects forensic metadata about how the user arrived at your site
 * 3. Attaches this metadata to GA4 as custom dimensions
 * 4. Initializes GA4 and sends the first pageview
 * 
 * CUSTOM DIMENSIONS TRACKED:
 * - traffic_origin: Where traffic came from with better direct/unknown distinction
 * - landing_depth: How deep in the site structure the user landed (0 = homepage, 1+ = subpages)
 * - entry_path: The exact path where the user first entered the site
 * - query_hints: Custom URL params that hint at source (pdf, share, ref, via)
 * - app_context: What platform/app is viewing the site (slack, teams, browser, etc.)
 * - first_touch: The original referrer with user agent context
 * - utm_source: UTM source parameter (most reliable marketing attribution)
 * - utm_campaign: UTM campaign parameter
 * - utm_medium: UTM medium parameter
 * - intelligent_source: **BEST** - Combines all signals for most accurate attribution
 * 
 * IMPROVED DIRECT TRAFFIC TRACKING:
 * ✅ This code now provides much better accuracy through:
 * 
 * 1. **Intelligent Source Detection**: The `intelligent_source` dimension combines:
 *    - UTM parameters (highest priority - explicitly set)
 *    - Referrer analysis (known sources like Google, LinkedIn, etc.)
 *    - User agent detection (app-based browsing)
 *    - Query parameter hints (shared links, PDFs)
 *    - Fallback to direct_unknown when no signals present
 * 
 * 2. **Better Referrer Handling**:
 *    - Empty referrer + app UA = "app_slack" / "app_teams" etc.
 *    - Empty referrer + no app = "direct_or_privacy_stripped"
 *    - Known referrer = specific source ("organic_google", "social_linkedin")
 *    - Unknown referrer = "other_referrer"
 * 
 * 3. **UTM Parameter Tracking**: Now captures utm_source, utm_medium, utm_campaign
 *    for reliable marketing attribution when present
 * 
 * HOW TO USE IN GA4:
 * - **Primary source**: Use `intelligent_source` for most accurate attribution
 * - **Marketing campaigns**: Filter by `utm_source`, `utm_campaign`, `utm_medium`
 * - **Direct traffic analysis**: Filter `intelligent_source` = "direct_unknown" and
 *   cross-reference with `landing_depth` (>2 = likely shared link) and `query_hints`
 * 
 * BEST PRACTICE:
 * Always use UTM parameters in your marketing links:
 * yoursite.com?utm_source=newsletter&utm_medium=email&utm_campaign=january2026
 */
export default function GAInitializer() {
    useEffect(() => {
        const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

        if (!measurementId) {
            console.warn("GA Measurement ID not set");
            return;
        }

        // Prevent multiple initializations
        if ((window as any)._gaInitialized) return;

        /* ---- Capture signals ONCE per session ---- */
        const traffic_origin = detectTrafficOrigin();
        const landing_depth = landingDepth();
        const entry_path = getEntryPath();
        const query_hints = detectQueryHints();
        const app_context = detectAppContext();
        const first_touch = getFirstTouch();
        
        // New improved tracking
        const utm_source = getUtmSource();
        const utm_campaign = getUtmCampaign();
        const utm_medium = getUtmMedium();
        const intelligent_source = getIntelligentSource();

        /* ---- Set consent preferences for GDPR/privacy compliance ---- */
        ReactGA.gtag("consent", "default", {
            ad_storage: "granted",
            ad_user_data: "granted",
            ad_personalization: "granted",
            analytics_storage: "granted",
        });

        /* ---- Attach forensic metadata to ALL subsequent GA events ---- */
        ReactGA.gtag("set", {
            // Original tracking
            traffic_origin,
            landing_depth,
            entry_path,
            query_hints,
            app_context,
            first_touch,
            
            // Improved tracking
            utm_source,
            utm_campaign,
            utm_medium,
            intelligent_source,  // This is the most accurate single source indicator
        });

        /* ---- Initialize Google Analytics 4 ---- */
        ReactGA.initialize(measurementId);

        // Mark as initialized to prevent duplicate tracking
        (window as any)._gaInitialized = true;

        /* ---- Send the initial pageview event ---- */
        ReactGA.send({
            hitType: "pageview",
            page: window.location.pathname,
        });
    }, []); // Empty dependency array = runs once on mount

    return null; // This is a side-effect component with no UI
}